import { useEffect, useCallback, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useFileStore, registerRefreshCallback, unregisterRefreshCallback } from '../stores/fileStore';
import { useUIStore } from '../stores/uiStore';
import { filesApi } from '../api/files';
import { foldersApi } from '../api/folders';
import { FileGrid } from '../components/files/FileGrid';
import { FileBreadcrumb } from '../components/files/FileBreadcrumb';
import { FileToolbar } from '../components/files/FileToolbar';
import { DragDropZone } from '../components/files/DragDropZone';
import { CreateFolderModal } from '../components/files/CreateFolderModal';
import { UploadModal } from '../components/files/UploadModal';
import { RenameModal } from '../components/files/RenameModal';
import { CreateLinkModal } from '../components/files/CreateLinkModal';
import { DeleteConfirmDialog } from '../components/files/DeleteConfirmDialog';
import { ContextMenuWrapper } from '../components/files/ContextMenu';
import { FilePreviewModal } from '../components/files/FilePreviewModal';
import { MoveToFolderModal } from '../components/files/MoveToFolderModal';
import { CreateDocumentModal } from '../components/documents/CreateDocumentModal';
import { Spinner } from '../components/ui/Spinner';

export function DashboardPage() {
  const { t } = useTranslation('files');
  const { folderId } = useParams<{ folderId: string }>();
  const location = useLocation();

  const {
    files,
    folders,
    isLoading,
    setFiles,
    setFolders,
    setCurrentFolderId,
    setCurrentProjectId,
    setCurrentClientId,
    setIsLoading,
    setBreadcrumb,
    sortBy,
    sortOrder,
    searchQuery,
  } = useFileStore();

  const isStarredPage = location.pathname === '/starred';
  const isTrashPage = location.pathname === '/trash';

  const isCreateDocumentModalOpen = useUIStore((s) => s.isCreateDocumentModalOpen);
  const closeCreateDocumentModal = useUIStore((s) => s.closeCreateDocumentModal);

  // Error state for load failures
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [filesRes, foldersRes] = await Promise.all([
        filesApi.list({
          folderId: isStarredPage || isTrashPage ? undefined : folderId || null,
          starred: isStarredPage || undefined,
          trashed: isTrashPage,
          search: searchQuery || undefined,
          sortBy,
          sortOrder,
        }),
        !isStarredPage && !isTrashPage
          ? foldersApi.list(folderId || null)
          : Promise.resolve({ folders: [] }),
      ]);

      setFiles(filesRes.files);
      setFolders(foldersRes.folders);

      // Load breadcrumb
      if (folderId) {
        const breadcrumbRes = await foldersApi.getBreadcrumb(folderId);
        setBreadcrumb(breadcrumbRes.breadcrumb);
      } else {
        setBreadcrumb([]);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      setLoadError(
        error instanceof Error ? error.message : 'Error al cargar archivos'
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    folderId,
    isStarredPage,
    isTrashPage,
    searchQuery,
    sortBy,
    sortOrder,
    setFiles,
    setFolders,
    setIsLoading,
    setBreadcrumb,
  ]);

  useEffect(() => {
    setCurrentFolderId(folderId || null);
    // Clear project/client context when in Dashboard (personal files)
    setCurrentProjectId(null);
    setCurrentClientId(null);
    loadData();
  }, [folderId, loadData, setCurrentFolderId, setCurrentProjectId, setCurrentClientId]);

  useEffect(() => {
    registerRefreshCallback(loadData);
    return () => {
      unregisterRefreshCallback();
    };
  }, [loadData]);

  const handleFilesDropped = (files: File[]) => {
    useUIStore.getState().openUploadModal(files);
  };

  const getPageTitle = () => {
    if (isStarredPage) return t('common:nav.starred');
    if (isTrashPage) return t('common:nav.trash');
    return t('common:nav.allFiles');
  };

  return (
    <DragDropZone onFilesDropped={handleFilesDropped}>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{getPageTitle()}</h1>
            <FileBreadcrumb />
          </div>
        </div>

        {/* Toolbar */}
        <FileToolbar />

        {/* Error banner */}
        {loadError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 dark:text-red-300 text-sm">{loadError}</span>
              <button
                onClick={loadData}
                className="ml-auto text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 underline text-sm font-medium"
              >
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : files.length === 0 && folders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20 text-center"
          >
            <div className="w-20 h-20 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-10 h-10 text-gray-400 dark:text-slate-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
              {isTrashPage ? t('empty.trashTitle') : t('empty.title')}
            </h3>
            <p className="text-gray-500 dark:text-slate-400 max-w-sm">
              {isTrashPage ? t('empty.trashDescription') : t('empty.description')}
            </p>
          </motion.div>
        ) : (
          <FileGrid />
        )}
      </div>

      {/* Modals */}
      <CreateFolderModal />
      <UploadModal />
      <RenameModal />
      <CreateLinkModal />
      <DeleteConfirmDialog />
      <ContextMenuWrapper />
      <FilePreviewModal />
      <MoveToFolderModal />
      <CreateDocumentModal
        isOpen={isCreateDocumentModalOpen}
        onClose={closeCreateDocumentModal}
        folderId={folderId || null}
      />
    </DragDropZone>
  );
}
